/**
 * @fileoverview Sidebar Component
 * @description Navigation sidebar with Alaska Airlines styling and RBAC filtering
 * @module components/layout/Sidebar
 */

import React, { useState, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, 
  ChevronLeft,
  ChevronRight,
  X,
  Github,
  CalendarDays,
  Ticket,
  FlaskConical,
  ExternalLink,
  Bug,
  PackageCheck,
  Database,
  Cpu,
  Bot,
  Shield,
  Lock,
  Brain,
  UsersRound,
  Activity,
  GitBranch,
  BarChart3,
  BookOpen,
  ArrowUpDown,
  Coins,
  Briefcase,
  Users,
  Building2,
  ListTree,
  Settings,
  MonitorDot,
  ScanSearch,
  ClipboardCheck,
  Radar,
  HeartPulse,
} from 'lucide-react';
import Logo from '../../common/Logo/Logo';
import { useAuth } from '@/context';
import { APP_CONFIG } from '@/config';
import { MODULES } from '@/constants';
import './Sidebar.css';

const Sidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const { hasModuleGrant, isAdmin, rbacLoaded } = useAuth();
  const location = useLocation();

  // Track which parent groups are expanded
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Auto-expand group if a child route is active
    const initial = {};
    if (location.pathname === '/github-metrics' || location.pathname === '/github-user-metrics' || location.pathname === '/copilot-cost' || location.pathname === '/lines-of-code' || location.pathname === '/ai-leverage' || location.pathname === '/project-savings-report') {
      initial['ai-metrics'] = true;
    }
    if (location.pathname === '/ai-evaluation-results') {
      initial['ai-evaluation'] = true;
    }
    if (location.pathname.startsWith('/scout-eval')) {
      initial['ai-evaluation'] = true;
    }
    if (location.pathname === '/model-context-protocol' || location.pathname === '/regression-optimizer' || location.pathname === '/pipeline-analysis' || location.pathname === '/pipeline-service') {
      initial['ai-usecase'] = true;
    }
    if (location.pathname === '/pnr-generator' || location.pathname === '/deadhead-pnr' || location.pathname === '/ai-test-data-generation') {
      initial['ai-test-data'] = true;
    }
    if (location.pathname.startsWith('/portfolio/') || location.pathname === '/flight-crew-deadletter') {
      initial['portfolio'] = true;
    }
    if (location.pathname === '/incident-analysis' || location.pathname === '/reports') {
      initial['prod-incident'] = true;
    }
    // Created or modified by CoPilot AI Agent
    if (location.pathname === '/azure-function-health') {
      initial['monitoring'] = true;
    }
    if (location.pathname.startsWith('/admin/')) {
      initial['admin-control'] = true;
    }
    return initial;
  });

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      // Accordion: collapse all others, toggle the clicked one
      if (prev[groupId]) {
        return {};
      }
      return { [groupId]: true };
    });
  };

  const baseMenuItems = [
    { 
      path: '/', 
      icon: LayoutDashboard, 
      label: 'Dashboard',
      exact: true,
      // No module requirement — visible to all authenticated users
    },
    // 1 - AI Metrics
    {
      id: 'ai-metrics',
      icon: BarChart3,
      label: 'AI Metrics',
      children: [
        {
          path: '/copilot-cost',
          icon: Coins,
          label: 'FinOps',
          module: MODULES.COPILOT_COST,
        },
        { 
          path: '/github-metrics', 
          icon: Github, 
          label: 'GHCP',
          module: MODULES.GITHUB_METRICS,
        },
        {
          path: '/lines-of-code',
          icon: Database,
          label: 'Lines of Code',
          module: MODULES.LINES_OF_CODE,
        },
        { 
          path: '/ai-leverage', 
          icon: Activity, 
          label: 'Test Generation',
          module: MODULES.TEST_AUTOMATION_LEVERAGE,
        },
        {
          path: '/github-user-metrics',
          icon: UsersRound,
          label: 'GH User Metrics',
          module: MODULES.GITHUB_USER_METRICS,
        },
        {
          path: '/project-savings-report',
          icon: Activity,
          label: 'Test Automation Leverage Report',
          module: MODULES.PIPELINE_SERVICE,
        },
      ],
    },
    // 2 - AI Evaluation
    {
      id: 'ai-evaluation',
      icon: ScanSearch,
      label: 'AI Evaluation',
      children: [
        {
          path: '/ai-evaluation-results',
          icon: ClipboardCheck,
          label: 'AI Evals',
        },
        {
          path: '/scout-eval',
          icon: Radar,
          label: 'Scout Evals',
        },
      ],
    },
    // 3 - AI Use Cases
    {
      id: 'ai-usecase',
      icon: Brain,
      label: 'AI Use Cases',
      children: [
        {
          path: 'https://genai-test-gen-app.azurewebsites.net/',
          icon: FlaskConical,
          label: 'TestCase Gen',
          external: true,
          hidden: !APP_CONFIG.features.enableTestGenerationLink,
        },
        {
          path: 'about:blank',
          icon: Bot,
          label: 'Agentic E2E Test Orchestrator',
          external: true,
        },
        { 
          path: '/model-context-protocol', 
          icon: Cpu, 
          label: 'AI Native Test Automation',
          module: MODULES.MODEL_CONTEXT_PROTOCOL,
        },
        {
          path: '/regression-optimizer',
          icon: PackageCheck,
          label: 'Regression Optimiser',
          module: MODULES.REGRESSION_OPTIMIZER,
        },
        {
          path: '/pipeline-analysis',
          icon: GitBranch,
          label: 'Pipeline Analysis',
          module: MODULES.PIPELINE_SERVICE,
        },
      ],
    },
    // 4 - AI Test Data Generator
    {
      id: 'ai-test-data',
      icon: Ticket,
      label: 'AI Test Data Generator',
      children: [
        { 
          path: '/pnr-generator', 
          icon: Ticket, 
          label: 'PNR Generator',
          module: MODULES.PNR_GENERATOR,
        },
        {
          path: '/deadhead-pnr',
          icon: ArrowUpDown,
          label: 'DH PNR Generator',
          module: MODULES.DEADHEAD_PNR,
        },
        {
          path: '/ai-test-data-generation',
          icon: Bot,
          label: 'Agentic Test Data Orchestrator',
          module: MODULES.AI_TEST_DATA_GENERATION,
        },
      ],
    },
    // 5 - Portfolio Solutions
    {
      id: 'portfolio',
      icon: Briefcase,
      label: 'Portfolio Solutions',
      children: [
        {
          path: '/portfolio/jcte',
          icon: Users,
          label: 'JCTE',
          module: MODULES.BUSINESS_PORTFOLIO,
        },
        {
          path: '/portfolio/airport-ops',
          icon: Building2,
          label: 'AirportOps',
          module: MODULES.BUSINESS_PORTFOLIO,
        },
      ],
    },
    // 6 - Production Incident Analysis
    {
      id: 'prod-incident',
      icon: Bug,
      label: 'Production Incident Analysis',
      children: [
        { 
          path: '/incident-analysis', 
          icon: Brain, 
          label: 'Incident Intelligence',
          module: MODULES.INCIDENT_ANALYSIS,
        },
        {
          path: '/reports',
          icon: BarChart3,
          label: 'Incident Analyser',
          module: MODULES.REPORTS,
        },
      ],
    },
    // Created or modified by CoPilot AI Agent
    {
      id: 'monitoring',
      icon: Activity,
      label: 'Monitoring',
      children: [
        {
          path: '/azure-function-health',
          icon: HeartPulse,
          label: 'Azure Function Health',
          module: MODULES.AZURE_FUNCTION_HEALTH,
        },
      ],
    },
    // --- Remaining items ---
    { 
      path: '/test-calendar', 
      icon: CalendarDays, 
      label: 'Test Calendar',
      module: MODULES.TEST_CALENDAR,
    },
    {
      path: 'https://alaskaair.sharepoint.com/sites/ITS_knowledge_management/BUSINESS/Forms/AllItems.aspx',
      icon: BookOpen,
      label: 'Alaska ITS KM Portal',
      external: true,
    },
  ];

  const menuItems = baseMenuItems;

  // Admin section — grouped under "Admin Control"
  const adminItems = [
    {
      id: 'admin-control',
      icon: Settings,
      label: 'Admin Control',
      adminOnly: true,
      children: [
        {
          path: '/admin/rbac',
          icon: Shield,
          label: 'Access Management',
          adminOnly: true,
        },
        {
          path: '/admin/app-insights',
          icon: MonitorDot,
          label: 'App Insights',
          adminOnly: true,
        },
      ],
    },
  ];

  const wipItems = [
    { icon: Bug, label: 'Production Defect RCA' },
    { icon: Database, label: 'Agentic Test Data Mgmt' },
  ];

  /** Filter menu items based on user's module access */
  // Show ALL modules in the sidebar (no filtering).
  // Non-granted modules display a lock icon; ProtectedRoute handles access denial.
  const visibleMenuItems = menuItems;

  const visibleAdminItems = isAdmin ? adminItems : [];

  const handleLinkClick = () => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  /** Add marquee class only when text actually overflows */
  const handleNavMouseEnter = useCallback((e) => {
    const label = e.currentTarget.querySelector('.nav-label');
    if (label && label.scrollWidth > label.clientWidth) {
      label.classList.add('marquee');
    }
  }, []);

  const handleNavMouseLeave = useCallback((e) => {
    const label = e.currentTarget.querySelector('.nav-label');
    if (label) {
      label.classList.remove('marquee');
    }
  }, []);

  return (
    <>
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="sidebar-overlay" role="presentation" onClick={() => setMobileOpen(false)} onKeyDown={(e) => { if (e.key === 'Escape') setMobileOpen(false); }} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Logo Section */}
        <div className="sidebar-header">
          <Logo collapsed={collapsed} />
          
          {/* Desktop Toggle */}
          <button 
            className="sidebar-toggle desktop-only"
            onClick={() => setCollapsed(!collapsed)}
            aria-label="Toggle sidebar"
          >
            <ChevronLeft className={collapsed ? 'rotated' : ''} />
          </button>

          {/* Mobile Close */}
          <button 
            className="sidebar-toggle mobile-only"
            onClick={() => setMobileOpen(false)}
            aria-label="Close sidebar"
          >
            <X />
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <ul className="nav-list">
            {visibleMenuItems.map((item) => {
              const Icon = item.icon;

              // Grouped parent item with children
              if (item.children) {
                const isExpanded = expandedGroups[item.id];
                const visibleChildren = item.children.filter(child => !child.hidden);
                const hasActiveChild = visibleChildren.some(child => location.pathname === child.path);
                return (
                  <li key={item.id} className={`nav-item nav-group ${isExpanded || hasActiveChild ? 'expanded' : ''}`} onMouseEnter={handleNavMouseEnter} onMouseLeave={handleNavMouseLeave}>
                    <button
                      className={`nav-link nav-group-toggle ${hasActiveChild ? 'active' : ''}`}
                      onClick={() => toggleGroup(item.id)}
                      aria-expanded={isExpanded || hasActiveChild}
                      aria-label={`${item.label}, submenu with ${visibleChildren.length} items`}
                    >
                      <Icon className="nav-icon" size={20} />
                      {!collapsed && (
                        <>
                          <span className="nav-label">{item.label}</span>
                          <span className="nav-group-menu-cue" aria-hidden="true">
                            <ListTree size={11} />
                            <span>{visibleChildren.length}</span>
                          </span>
                          <ChevronRight className={`nav-group-chevron ${isExpanded || hasActiveChild ? 'rotated' : ''}`} size={14} />
                        </>
                      )}
                    </button>
                    {!collapsed && (
                      <ul className={`nav-list nav-children ${isExpanded || hasActiveChild ? 'children-open' : 'children-closed'}`}>
                        {visibleChildren.map((child) => {
                          const ChildIcon = child.icon;
                          const isChildLocked = child.module && rbacLoaded && !hasModuleGrant(child.module);
                          if (child.external) {
                            return (
                              <li key={child.path} className="nav-item" onMouseEnter={handleNavMouseEnter} onMouseLeave={handleNavMouseLeave}>
                                <a
                                  href={child.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="nav-link nav-child-link"
                                  onClick={handleLinkClick}
                                >
                                  <ChildIcon className="nav-icon" size={18} />
                                  <span className="nav-label">
                                    {child.label}
                                    <ExternalLink className="external-icon" size={14} />
                                  </span>
                                </a>
                              </li>
                            );
                          }
                          return (
                            <li key={child.path} className="nav-item" onMouseEnter={handleNavMouseEnter} onMouseLeave={handleNavMouseLeave}>
                              <NavLink
                                to={child.path}
                                className={({ isActive }) =>
                                  `nav-link nav-child-link ${isActive ? 'active' : ''} ${isChildLocked ? 'locked' : ''}`
                                }
                                onClick={handleLinkClick}
                              >
                                <ChildIcon className="nav-icon" size={18} />
                                <span className="nav-label">
                                  {child.label}
                                  {isChildLocked && <Lock className="lock-icon" size={12} />}
                                </span>
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }

              const isLocked = item.module && rbacLoaded && !hasModuleGrant(item.module);
              return (
                <li key={item.path} className="nav-item" onMouseEnter={handleNavMouseEnter} onMouseLeave={handleNavMouseLeave}>
                  {item.external ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-link"
                      onClick={handleLinkClick}
                    >
                      <Icon className="nav-icon" size={20} />
                      {!collapsed && (
                        <span className="nav-label">
                          {item.label}
                          <ExternalLink className="external-icon" size={14} />
                        </span>
                      )}
                    </a>
                  ) : (
                    <NavLink
                      to={item.path}
                      end={item.exact}
                      className={({ isActive }) => 
                        `nav-link ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`
                      }
                      onClick={handleLinkClick}
                    >
                      <Icon className="nav-icon" size={20} />
                      {!collapsed && (
                        <span className="nav-label">
                          {item.label}
                          {isLocked && <Lock className="lock-icon" size={12} />}
                        </span>
                      )}
                    </NavLink>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Admin Section */}
          {visibleAdminItems.length > 0 && (
            <ul className="nav-list">
              {!collapsed && (
                <li className="nav-section-label">
                  <span className="nav-label" style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, padding: '8px 12px' }}>
                    Admin
                  </span>
                </li>
              )}
              {visibleAdminItems.map((item) => {
                const Icon = item.icon;

                // Grouped admin item with children
                if (item.children) {
                  const isExpanded = expandedGroups[item.id];
                  const hasActiveChild = item.children.some(child => location.pathname === child.path);
                  return (
                    <li key={item.id} className={`nav-item nav-group ${isExpanded || hasActiveChild ? 'expanded' : ''}`} onMouseEnter={handleNavMouseEnter} onMouseLeave={handleNavMouseLeave}>
                      <button
                        className={`nav-link nav-group-toggle ${hasActiveChild ? 'active' : ''}`}
                        onClick={() => toggleGroup(item.id)}
                        aria-expanded={isExpanded || hasActiveChild}
                        aria-label={`${item.label}, submenu with ${item.children.length} items`}
                      >
                        <Icon className="nav-icon" size={20} />
                        {!collapsed && (
                          <>
                            <span className="nav-label">{item.label}</span>
                            <span className="nav-group-menu-cue" aria-hidden="true">
                              <ListTree size={11} />
                              <span>{item.children.length}</span>
                            </span>
                            <ChevronRight className={`nav-group-chevron ${isExpanded || hasActiveChild ? 'rotated' : ''}`} size={14} />
                          </>
                        )}
                      </button>
                      {!collapsed && (
                        <ul className={`nav-list nav-children ${isExpanded || hasActiveChild ? 'children-open' : 'children-closed'}`}>
                          {item.children.map((child) => {
                            const ChildIcon = child.icon;
                            return (
                              <li key={child.path} className="nav-item" onMouseEnter={handleNavMouseEnter} onMouseLeave={handleNavMouseLeave}>
                                <NavLink
                                  to={child.path}
                                  className={({ isActive }) =>
                                    `nav-link nav-child-link ${isActive ? 'active' : ''}`
                                  }
                                  onClick={handleLinkClick}
                                >
                                  <ChildIcon className="nav-icon" size={18} />
                                  <span className="nav-label">{child.label}</span>
                                </NavLink>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                }

                // Flat admin item (fallback)
                return (
                  <li key={item.path} className="nav-item">
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `nav-link ${isActive ? 'active' : ''}`
                      }
                      onClick={handleLinkClick}
                    >
                      <Icon className="nav-icon" size={20} />
                      {!collapsed && <span className="nav-label">{item.label}</span>}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          )}

          {/* WIP Items */}
          <ul className="nav-list">
            {wipItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.label} className="nav-item">
                  <span className="nav-link wip-link" title="Work in Progress">
                    <Icon className="nav-icon" size={20} />
                    {!collapsed && <span className="nav-label">{item.label}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        {!collapsed && (
          <div className="sidebar-footer">
            <div className="footer-content">
              <p className="footer-text">Alaska Airlines</p>
              <p className="footer-subtext">Enterprise QE</p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export { Sidebar };
export default Sidebar;
